import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';

import MaterialTable from 'material-table';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from '@material-ui/core';
import moment from 'moment';
import { StockOrder } from '../../models';
import { PaginatedResponse } from '../../../shared/models';

import { bookStockOrder, finalizeStockOrder, getStockOrdersByPage } from '../../api';
import { checkStatus, parseJSON, handleError } from '../../../shared/api/core/common';
import { SVG_ICONS } from '../../../shared/icons/SvgIcons';
import { ErrorStatusBar, CustomTableTitle } from '../../../shared/components';
import { Toast, enqueueSnackbar, ToastSuccess, ToastError } from '../../../shared/components/Notifier';
import { EMPTY_ROW_MSG, ERROR_500, ERROR_MSG, NO_PERMISSIONS_ERROR_MSG } from '../../../shared/lib/Localization';
import { initQueryParams, replaceHistory, useQuery } from '../../../shared/lib/TableQueryParams';
import { BusinessRouterPaths } from '../../../page/Routes/RouterPaths';
import { hasPermission } from '../../../shared/lib/PermissionManager';

const navigateToCatalogOrder = (
    history: { push: (path: string, state?: { pageMode: string }) => void },
    uuid: string,
    pageMode: 'view_mode' | 'edit_mode',
) => {
    history.push(
        BusinessRouterPaths.editCatalogOrder.replace(':stockOrderUuid', uuid),
        { pageMode },
    );
};

const StockOrderListScreen = (props: any) => {
    const [emptyDataSourceMessage, setEmptyDataSourceMessage] = useState(EMPTY_ROW_MSG);
    const [isErrorStatusVisible, setErrorStatusVisible] = useState(false);
    let locationQuery = useQuery(useLocation);
    const [tableQuery, setTableQuery] = useState(initQueryParams(locationQuery));
    const tableRef = useRef<{ onQueryChange: any; }>();
    const [isLoading, setLoading] = useState(false);
    const [bookOrder, setBookOrder] = useState<StockOrder | null>(null);
    const [isBooking, setIsBooking] = useState(false);
    const [finalizeOrder, setFinalizeOrder] = useState<StockOrder | null>(null);
    const [isFinalizing, setIsFinalizing] = useState(false);

    const authUser = props.userData?.userData?.userData;
    const isSuperUser = authUser?.is_superuser;
    const userPermissions = authUser?.user_permissions || [];
    const canFinalize = hasPermission(userPermissions, 'finalize_stockorder', isSuperUser);
    const canBook = hasPermission(userPermissions, 'book_stockorder', isSuperUser);

    useEffect(() => {
        if (!isLoading) {
            tableRef.current && tableRef.current.onQueryChange();
        }
    }, [props.userData.selectedBusiness]);

    const showToast = (message: string, options = ToastError) => {
        props.showToast({ message, options });
    };

    const isBooked = (row: StockOrder) => Boolean(row && row.bookingTime);
    const isFinalized = (row: StockOrder) => Boolean(row && (row as any).finalizedAt);

    const handleFinalizeConfirm = () => {
        if (!finalizeOrder || !finalizeOrder.uuid) {
            return;
        }
        setIsFinalizing(true);
        finalizeStockOrder(finalizeOrder.uuid, props.userData.selectedBusiness.business_id)
            .catch(handleError)
            .then(checkStatus)
            .then(parseJSON)
            .then(() => {
                setIsFinalizing(false);
                setFinalizeOrder(null);
                showToast('Stock order finalized successfully.', ToastSuccess);
                tableRef.current && tableRef.current.onQueryChange();
            })
            .catch((error: any) => {
                setIsFinalizing(false);
                let msg = error && error.body && error.body.error
                    ? error.body.error
                    : 'Failed to finalize stock order.';
                if (error && (error.status === 403 || error.status === 401)) {
                    msg = NO_PERMISSIONS_ERROR_MSG;
                }
                showToast(msg);
            });
    };

    const handleBookConfirm = () => {
        if (!bookOrder || !bookOrder.uuid) {
            return;
        }
        setIsBooking(true);
        bookStockOrder(bookOrder.uuid, props.userData.selectedBusiness.business_id)
            .catch(handleError)
            .then(checkStatus)
            .then(parseJSON)
            .then(() => {
                setIsBooking(false);
                setBookOrder(null);
                showToast('Stock order booked successfully.', ToastSuccess);
                tableRef.current && tableRef.current.onQueryChange();
            })
            .catch((error: any) => {
                setIsBooking(false);
                let msg = error && error.body && error.body.error
                    ? error.body.error
                    : 'Failed to book stock order.';
                if (error && (error.status === 403 || error.status === 401)) {
                    msg = NO_PERMISSIONS_ERROR_MSG;
                }
                showToast(msg);
            });
    };

    return (
        <div>
            <ErrorStatusBar isVisible={isErrorStatusVisible} />
            <div style={{ maxWidth: '100%' }}>
                <MaterialTable
                    options={{
                        debounceInterval: 650,
                        pageSize: 10,
                        pageSizeOptions: [10, 50, 100, 500, 1000],
                        headerStyle: {
                            backgroundColor: '#eee',
                        },
                        thirdSortClick: false,
                        draggable: false,
                        sorting: true,
                        actionsColumnIndex: -1,
                    }}
                    tableRef={tableRef}
                    localization={{
                        body: {
                            emptyDataSourceMessage: emptyDataSourceMessage
                        }
                    }}
                    columns={[
                        {
                            title: 'Date',
                            field: 'createTime',
                            render: rowData => (
                                rowData.createTime ?
                                    <span>{moment(rowData.createTime).format('YYYY-MM-DD HH:mm')}</span>
                                    : <span></span>
                            ),
                        },
                        { title: 'Number', field: 'number' },
                        {
                            title: 'Items',
                            field: 'items',
                            render: rowData => {
                                if (rowData && rowData.items) {
                                    return <span>{rowData.items.length}</span>;
                                }
                                return <span></span>;
                            },
                        },
                        {
                            title: 'Warehouse',
                            field: 'target',
                            render: rowData => {
                                const target: any = rowData && rowData.target;
                                if (target && target.name) {
                                    return <span>{target.name}</span>;
                                }
                                return <span></span>;
                            },
                        },
                        {
                            title: 'Supplier',
                            field: 'supplier',
                            render: rowData => {
                                const supplier: any = rowData && rowData.supplier;
                                if (supplier && supplier.name) {
                                    return <span>{supplier.name}</span>;
                                }
                                return <span></span>;
                            },
                        },
                        {
                            title: 'Description',
                            field: 'description',
                        },
                        {
                            title: 'Booked',
                            field: 'bookingTime',
                            render: rowData => (
                                rowData.bookingTime
                                    ? <span>{moment(rowData.bookingTime).format('YYYY-MM-DD HH:mm')}</span>
                                    : <span>Open</span>
                            ),
                        },
                        {
                            title: 'Supplier Notified',
                            field: 'supplierNotifiedAt',
                            sorting: false,
                            render: rowData => {
                                const notifiedAt = (rowData as any).supplierNotifiedAt;
                                return notifiedAt
                                    ? <span>{moment(notifiedAt).format('YYYY-MM-DD HH:mm')}</span>
                                    : <span>—</span>;
                            },
                        },
                    ]}
                    data={query =>
                        new Promise((resolve, reject) => {
                            if (!isLoading) {
                                const sb = props.userData.selectedBusiness;
                                if (sb && sb.business_id) {
                                    replaceHistory(query, props.history);
                                    let pageData = {
                                        data: [] as any[],
                                        totalCount: 0,
                                        page: query.page
                                    };
                                    setLoading(true);
                                    let orderBy = '';
                                    if (query.orderBy) {
                                        orderBy = query.orderBy.field as string;
                                        orderBy = `${query.orderDirection && query.orderDirection === 'desc' ? '-' : ''}${orderBy}`;
                                    }
                                    getStockOrdersByPage(
                                        sb.business_id,
                                        query.page + 1,
                                        query.pageSize,
                                        query.search,
                                        orderBy
                                    )
                                        .catch(handleError)
                                        .then(checkStatus)
                                        .then(parseJSON)
                                        .then((data: PaginatedResponse<any>) => {
                                            setErrorStatusVisible(false);
                                            setEmptyDataSourceMessage(EMPTY_ROW_MSG);
                                            pageData = {
                                                data: data.results,
                                                totalCount: data.count,
                                                page: query.page
                                            };
                                            setTableQuery({
                                                ...tableQuery,
                                                orderBy,
                                                totalCount: data.count,
                                                page: query.page,
                                                pageSize: query.pageSize
                                            });
                                            setLoading(false);
                                            resolve(pageData);
                                        })
                                        .catch((error: any) => {
                                            if (error) {
                                                if (error.status && error.status === 500) {
                                                    setEmptyDataSourceMessage(ERROR_500);
                                                    setErrorStatusVisible(false);
                                                } else if ((error.status === 403 || error.status === 401)
                                                ) {
                                                    setEmptyDataSourceMessage(ERROR_MSG);
                                                    setErrorStatusVisible(true);
                                                } else {
                                                    setEmptyDataSourceMessage(ERROR_MSG);
                                                    setErrorStatusVisible(false);
                                                }
                                            }
                                            setTableQuery({
                                                ...tableQuery,
                                                totalCount: 0,
                                                page: query.page,
                                                pageSize: query.pageSize
                                            });
                                            setLoading(false);
                                            resolve(pageData);
                                        });
                                }
                            }
                        })
                    }
                    title={
                        <CustomTableTitle
                            history={props.history}
                            title={'Stock Orders'}
                            icon={SVG_ICONS.stockOrder} />
                    }
                    actions={[
                        {
                            icon: 'refresh',
                            tooltip: 'Refresh Data',
                            isFreeAction: true,
                            onClick: () => tableRef.current && tableRef.current.onQueryChange(),
                        },
                        {
                            icon: 'list',
                            tooltip: 'View Items',
                            onClick: (_event, rowData) => {
                                const row = Array.isArray(rowData) ? rowData[0] : rowData;
                                const order = row as StockOrder;
                                if (!order || !order.uuid) {
                                    return;
                                }
                                navigateToCatalogOrder(props.history, order.uuid as string, 'view_mode');
                            },
                        },
                        (rowData: StockOrder) => ({
                            icon: 'edit',
                            tooltip: isBooked(rowData)
                                ? 'Cannot edit a booked stock order'
                                : 'Edit stock order',
                            disabled: isBooked(rowData),
                            onClick: (_event: any, row: StockOrder | StockOrder[]) => {
                                const order = Array.isArray(row) ? row[0] : row;
                                if (!order || !order.uuid || isBooked(order)) {
                                    return;
                                }
                                navigateToCatalogOrder(props.history, order.uuid as string, 'edit_mode');
                            },
                        }),
                        (rowData: StockOrder) => ({
                            icon: 'check_circle',
                            tooltip: isFinalized(rowData)
                                ? 'Stock order already finalized'
                                : 'Finalize stock order',
                            hidden: !canFinalize,
                            disabled: isFinalized(rowData) || isBooked(rowData),
                            onClick: (_event: any, row: StockOrder | StockOrder[]) => {
                                const order = Array.isArray(row) ? row[0] : row;
                                if (!order || isFinalized(order) || isBooked(order)) {
                                    return;
                                }
                                setFinalizeOrder(order);
                            },
                        }),
                        (rowData: StockOrder) => ({
                            icon: 'bookmark',
                            tooltip: isBooked(rowData)
                                ? 'Stock order already booked'
                                : 'Book stock order',
                            hidden: !canBook,
                            disabled: isBooked(rowData),
                            onClick: (_event: any, row: StockOrder | StockOrder[]) => {
                                const order = Array.isArray(row) ? row[0] : row;
                                if (!order || isBooked(order)) {
                                    return;
                                }
                                setBookOrder(order);
                            },
                        }),
                    ]}
                />
            </div>
            <Dialog open={Boolean(finalizeOrder)} onClose={() => !isFinalizing && setFinalizeOrder(null)}>
                <DialogTitle>Finalize stock order</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {finalizeOrder
                            ? `Finalize stock order #${finalizeOrder.number}? Depending on this business's workflow this may also notify the supplier and book the order.`
                            : ''}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setFinalizeOrder(null)} disabled={isFinalizing}>
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={handleFinalizeConfirm}
                        disabled={isFinalizing}
                    >
                        {isFinalizing ? <CircularProgress size={20} /> : 'Finalize'}
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={Boolean(bookOrder)} onClose={() => !isBooking && setBookOrder(null)}>
                <DialogTitle>Book stock order</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {bookOrder
                            ? `Book stock order #${bookOrder.number}? This cannot be undone from this screen.`
                            : ''}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBookOrder(null)} disabled={isBooking}>
                        Cancel
                    </Button>
                    <Button
                        color="primary"
                        variant="contained"
                        onClick={handleBookConfirm}
                        disabled={isBooking}
                    >
                        {isBooking ? <CircularProgress size={20} /> : 'Book'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

const mapStateToProps = (state: any) => {
    return {
        userData: state.userLocalData,
    };
};

const mapDispatchToProps = (dispatch: any) => ({
    showToast: (toast: Toast) => dispatch(enqueueSnackbar(toast)),
});

export default connect(mapStateToProps, mapDispatchToProps)(StockOrderListScreen);
